import React, {useEffect, useState, useRef, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import usePageTitle from '../../../../hooks/usePageTitle';
import {toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchResources,
  addResource,
  patchResource,
  deleteResource,
  selectResources,
  fetchResourceManagerDetails,
  selectResourceManagerDetails,
  fetchUsers,
  selectUsersList,
  isMarketOwnerAccount,
  clearUserMessages,
} from '../../../../slices/userSlice';
import {
  selectAuthIsAdmin,
  selectAuthIsMarketOwner,
  selectAuthIsMarketOwnerReadOnly
} from '../../../../slices/authSlice';
import { selectAuthUser } from '../../../../slices/authSlice';
import {selectUserDetails} from '../../../../slices/userSlice';
import Pagination from '../../../../components/Pagination';
import {useBreadcrumbs} from '../../../../contexts/BreadcrumbContext';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import { axiosInstance } from '../../../../routes/axiosInstance';

// New component imports
import AddEditResourceForm from './components/AddEditResourceForm';
import FiltersPanel from './components/FiltersPanel';
import StatsCard from './components/StatsCard';
import ResourceListHeader from './components/ResourceListHeader';
import ResourceList from './components/ResourceList';
import ManagersModalComponent from './components/ManagersModal';
import AllManagersModalComponent from './components/AllManagersModal';

// Utility helpers
import {
  normalizeManager,
  formatRoleLabel as utilFormatRoleLabel,
  isMine as utilIsMine,
  getManagerEntry as utilGetManagerEntry,
  getManagerEntries as utilGetManagerEntries,
} from './resourceUtils';

// Writable shape of the add/edit resource form, mirroring the editable fields
// of users.UserResources (api/users/models/user_resources.py).
const EMPTY_RESOURCE_FORM = {
  name: '',
  timezone: 'Europe/Brussels',
  units: 'mw',
  use_case: '',
  user: '',
  target_summary: '',
  target_note: '',
  source_name: '',
  source_url: '',
  resolution_minutes: 15,
  program_slug: '',
  monthly_budget_intraday: '3500.00',
  monthly_budget_d1: '3500.00',
  monthly_budget_dn: '0.00',
  min_payout_threshold: '1000.00',
  // Ensemble forecast floor: clip_enabled=false maps to forecast_floor=null
  // (no clipping, for signed targets such as net mFRR energy).
  forecast_floor: '0',
  forecast_floor_enabled: true,
  is_active: true,
};

const resourceToForm = (r) => ({
  ...EMPTY_RESOURCE_FORM,
  name: r.name || '',
  timezone: r.timezone || 'Europe/Brussels',
  units: r.units || 'mw',
  use_case: r.use_case || '',
  user: r.user || '',
  target_summary: r.target_summary || '',
  target_note: r.target_note || '',
  source_name: r.source_name || '',
  source_url: r.source_url || '',
  resolution_minutes: r.resolution_minutes ?? 15,
  program_slug: r.program_slug || '',
  monthly_budget_intraday: r.monthly_budget_intraday ?? '3500.00',
  monthly_budget_d1: r.monthly_budget_d1 ?? '3500.00',
  monthly_budget_dn: r.monthly_budget_dn ?? '0.00',
  min_payout_threshold: r.min_payout_threshold ?? '1000.00',
  forecast_floor:
    r.forecast_floor === null || r.forecast_floor === undefined
      ? '0'
      : String(r.forecast_floor),
  forecast_floor_enabled: r.forecast_floor !== null && r.forecast_floor !== undefined,
  is_active: r.is_active !== undefined ? r.is_active : true,
});

export default function ResourcesMain() {
  usePageTitle('Resources');
  const dispatch = useDispatch();
  const resourcesRaw = useSelector(selectResources);
  const resources = useMemo(() => resourcesRaw || [], [resourcesRaw]);
  const isAdmin = useSelector(selectAuthIsAdmin);
  useBreadcrumbs(
    isAdmin
      ? [{ label: 'Resources' }, { label: 'Manage Resources' }]
      : [{ label: 'Resources' }],
    isAdmin
      ? 'Configure and manage forecasting resources'
      : 'Browse forecasting resources and stats'
  );
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const authUser = useSelector(selectAuthUser);
  const userDetails = useSelector(selectUserDetails);
  const resourceManagerDetailsRaw = useSelector(selectResourceManagerDetails);
  const resourceManagerDetails = useMemo(() => resourceManagerDetailsRaw || [], [resourceManagerDetailsRaw]);
  const usersList = useSelector(selectUsersList);

  // Candidate owners: full market owners only (non-forecaster, non-admin,
  // non-read-only — viewers are excluded).
  const ownerOptions = useMemo(() => (
    (usersList || [])
      .filter(u => isMarketOwnerAccount(u) && u.is_read_only !== true)
      .map(u => {
        const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
        return { value: u.id, label: name ? `${u.email} (${name})` : u.email };
      })
  ), [usersList]);

  // Resource creation visibility and state logic:
  // - only admins (superusers) can add/edit/delete resources
  // - market owners and forecasters get a read-only view (filters + stats + list)
  const showAddResource = isAdmin;
  const isAddResourceEnabled = isAdmin;

  // UI state
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null); // inline metadata expansion
  const [filterMode, setFilterMode] = useState('all'); // all | mine | others
  const [useCaseFilter, setUseCaseFilter] = useState('all'); // all | solar_power | wind_power
  // Deactivated resources are retired markets: kept for their history, not
  // something you act on. Hidden by default, with the count still shown in
  // Stats so nothing disappears silently.
  const [hideInactive, setHideInactive] = useState(true);
  const [sortBy, setSortBy] = useState('name'); // name | registered_at
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [resourceOrder, setResourceOrder] = useState([]); // Track resource display order
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Resource to delete confirmation
  const [managersLoading, setManagersLoading] = useState(false);
  const [managersModalOpen, setManagersModalOpen] = useState(false);
  const [modalManagersResource, setModalManagersResource] = useState(null);
  const [allManagersModalOpen, setAllManagersModalOpen] = useState(false);
  const [perResourceManagers, setPerResourceManagers] = useState([]);
  const [perResourceManagersLoading, setPerResourceManagersLoading] = useState(false);

  const searchRef = useRef(null);
  const nameRef = useRef(null);


  const [form, setForm] = useState(EMPTY_RESOURCE_FORM);

  // Fetch resources and resource manager details
  useEffect(() => {
    if (!(resources && resources.length > 0)) dispatch(fetchResources());
    // Fetch resource manager details to know which resources the user manages
    // include admins as well — admins can manage resources too
    if ((isMarketOwner || isMarketOwnerReadOnly || isAdmin)) {
      // Always refresh manager details for the current authenticated user.
      // Use an async flow so we can set a loading flag and avoid showing stale badges
      let cancelled = false;
      const load = async () => {
        try {
          setManagersLoading(true);
          await dispatch(fetchResourceManagerDetails()).unwrap?.() || await dispatch(fetchResourceManagerDetails());
        } catch (e) {
          // ignore fetch errors here - UI will show no managers
        } finally {
          if (!cancelled) setManagersLoading(false);
        }
      };
      load();
      return () => { cancelled = true; };
    }
  }, [dispatch, resources, isMarketOwner, isMarketOwnerReadOnly, isAdmin, authUser]);

  // Load market owners for the owner picker (admins only).
  useEffect(() => {
    if (isAdmin) dispatch(fetchUsers());
  }, [dispatch, isAdmin]);

  // Clear messages on mount and unmount - separate effect with no dependencies
  useEffect(() => {
    // Clear any stale user messages on mount (from other pages)
    dispatch(clearUserMessages());
    toast.dismiss(); // Dismiss any lingering toasts from other pages

    // Listen for page visibility changes and route changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is being hidden (navigating away), dismiss all toasts immediately
        toast.dismiss();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clear messages and all active toasts when component unmounts
    return () => {
      toast.dismiss(); // Dismiss all active toast notifications immediately
      dispatch(clearUserMessages());
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch]);

  // Initialize resource order on first load
  useEffect(() => {
    if (resources && resources.length > 0 && resourceOrder.length === 0) {
      setResourceOrder(resources.map(r => r.id));
    }
  }, [resources, resourceOrder.length]);

  // Apply sorting to resources
  const sortedResources = React.useMemo(() => {
    if (!resources || resources.length === 0) return resources;

    return [...resources].sort((a, b) => {
      if (sortBy === 'name') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      } else if (sortBy === 'registered_at') {
        const dateA = a.registered_at ? new Date(a.registered_at) : new Date(0);
        const dateB = b.registered_at ? new Date(b.registered_at) : new Date(0);
        return dateB - dateA; // Most recent first
      }
      return 0;
    });
  }, [resources, sortBy]);

  useEffect(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) setFiltered(sortedResources);
    else setFiltered(sortedResources.filter(r => (
      (r.name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.id || '').toLowerCase().includes(q) ||
      (r.use_case || '').toLowerCase().includes(q)
    )));
  }, [sortedResources, search]);

  // helper to determine resource ownership - includes owned resources AND managed resources
  const isMine = (r) => utilIsMine(r, userDetails, resourceManagerDetails);

  // Group managers by resource key for the "All Managers" modal
  const groupedManagers = React.useMemo(() => {
    const map = new Map();
    (resourceManagerDetails || []).forEach(item => {
      const key = item?.resource !== undefined && item?.resource !== null ? String(item.resource) : (item?.resource_name || 'Unspecified');
      const label = item?.resource_name || item?.resource_label || key;
      if (!map.has(key)) map.set(key, { label, entries: [] });
      map.get(key).entries.push(item);
    });
    return map; // Map<resourceKey, {label, entries: [...] }>
  }, [resourceManagerDetails]);

  // Return the resource manager record for a resource (if any)
  const getManagerEntry = (r) => utilGetManagerEntry(r, userDetails, resourceManagerDetails);

  // Return all manager entries for a resource (may be multiple managers)
  const getManagerEntries = (r) => utilGetManagerEntries(r, resourceManagerDetails);

  // Format the role label into a human-friendly title case (e.g. 'resource_manager' -> 'Resource Manager')
  const formatRoleLabel = (manager) => utilFormatRoleLabel(manager);

  const visibleResources = (filtered || []).filter(r => {
    // Apply ownership filter
    let ownershipMatch = true;
    if (filterMode === 'mine') ownershipMatch = isMine(r);
    if (filterMode === 'others') ownershipMatch = !isMine(r);

    // Apply use case filter
    let useCaseMatch = true;
    if (useCaseFilter !== 'all') {
      useCaseMatch = r.use_case === useCaseFilter;
    }

    // Hide deactivated resources unless asked for. A resource the user is
    // editing stays visible, so deactivating one from the form does not make
    // the card vanish mid-edit.
    const activeMatch = !hideInactive
      || r.is_active !== false
      || (editing && editing.id === r.id);

    return ownershipMatch && useCaseMatch && activeMatch;
  });

  // Pagination calculations
  const totalItems = visibleResources.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedResources = visibleResources.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterMode, useCaseFilter, sortBy, hideInactive]);

  // Ensure current page doesn't exceed total pages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (editing) {
      setForm(resourceToForm(editing));
    } else {
      setForm(EMPTY_RESOURCE_FORM);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);

  const openEdit = (r, opts = {}) => {
    if (!isAddResourceEnabled) return;
    setEditing(r);
    // if keepExpanded flag is provided, keep the expanded view for this resource
    if (opts.keepExpanded) {
      setExpandedId(r?.id);
    } else {
      setExpandedId(null);
    }
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!isAddResourceEnabled) {
      toast.error('You do not have permission to perform this action');
      return;
    }
    try {
      // Drop an empty owner so create defaults to the superuser and PATCH
      // doesn't try to null out the FK.
      const payload = {...form};
      if (!payload.user) delete payload.user;
      // Fold the clip toggle into the API field: disabled -> null (no
      // clipping). The field is superuser-only and not accepted on create;
      // new resources start at the default floor (0).
      if (editing) {
        payload.forecast_floor = payload.forecast_floor_enabled
          ? (parseFloat(payload.forecast_floor) || 0)
          : null;
      } else {
        delete payload.forecast_floor;
      }
      delete payload.forecast_floor_enabled;
      if (editing) {
        const result = await dispatch(patchResource({resourceId: editing.id, updates: payload}));
        if (patchResource.rejected.match(result)) {
          toast.error(result.payload || 'Update failed');
          return;
        }

        // Refresh resources and update form with fresh server data
        const fetchResult = await dispatch(fetchResources());
        if (fetchResult.payload?.data) {
          const updatedResource = fetchResult.payload.data.find(r => r.id === editing.id);
          if (updatedResource) {
            setEditing(updatedResource);
            setForm(resourceToForm(updatedResource));
          }
        }
        toast.success('Resource updated');
      } else {
        const result = await dispatch(addResource(payload));
        if (addResource.rejected.match(result)) {
          toast.error(result.payload || 'Create failed');
          return;
        }
        toast.success('Resource created');
        const fetchResult = await dispatch(fetchResources());
        // Add new resource to order tracking
        if (fetchResult.payload?.data && result.payload?.id) {
          setResourceOrder(prev => [...prev, result.payload.id]);
        }
        setForm(EMPTY_RESOURCE_FORM);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Operation failed');
    }
  };

  const handleDelete = (r) => {
    setDeleteConfirm(r);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      const result = await dispatch(deleteResource(deleteConfirm.id));
      if (deleteResource.rejected.match(result)) {
        toast.error(result.payload || 'Delete failed');
        return;
      }
      toast.success('Resource deleted');
      dispatch(fetchResources());
      // Remove deleted resource from order tracking
      setResourceOrder(prev => prev.filter(id => id !== deleteConfirm.id));
      if (expandedId === deleteConfirm.id) setExpandedId(null);
      if (editing && editing.id === deleteConfirm.id) setEditing(null);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const toggleExpand = (id) => {
    // If the clicked id is already expanded => we're collapsing it: clear editing for that resource
    if (expandedId === id) {
      setExpandedId(null);
      if (editing && editing.id === id) setEditing(null);
      return;
    }
    // Otherwise expand the new id
    setExpandedId(id);
  };

  const copyId = (id) => {
    try {
      navigator.clipboard.writeText(id);
      toast.info('Copied ID');
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const copyJSON = (r) => {
    try {
      navigator.clipboard.writeText(JSON.stringify(r, null, 2));
      toast.info('Copied JSON');
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };


  return (
    <div className="space-y-6">
      {/* 2-Column Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:items-stretch">
        {/* Left Column - Actions. Below lg it drops under the list so a
            forecaster meets resources first, not a screenful of filters. The
            add/edit form keeps its place above the list for admins. */}
        <div className={`lg:col-span-1 flex min-w-0 ${showAddResource ? '' : 'order-2 lg:order-none'}`}>
          <div className="rounded-xl bg-surface dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-sm divide-y divide-gray-100 dark:divide-gray-700 w-full min-w-0">
            {showAddResource && (
              <AddEditResourceForm
                form={form}
                onChange={setForm}
                onSubmit={handleSubmit}
                editing={editing}
                ownerOptions={ownerOptions}
                isEnabled={isAddResourceEnabled}
                onCancel={() => { setEditing(null); setForm(EMPTY_RESOURCE_FORM); }}
                nameRef={nameRef}
              />
            )}

            {/* Below lg the same filters ride above the list as a chip strip
                (ResourceListHeader), so this panel would be a duplicate. */}
            <div className="hidden lg:block">
              <FiltersPanel
                filterMode={filterMode}
                setFilterMode={setFilterMode}
                resources={resources}
                isMine={isMine}
                hideInactive={hideInactive}
                setHideInactive={setHideInactive}
              />
            </div>
            <StatsCard resources={resources} />
          </div>
        </div>

        {/* Right Column - Resource List */}
        <div className={`lg:col-span-2 flex min-w-0 ${showAddResource ? '' : 'order-1 lg:order-none'}`}>
          <div className="rounded-xl bg-surface dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-sm w-full min-w-0 flex flex-col">
            <ResourceListHeader
              search={search}
              setSearch={setSearch}
              useCaseFilter={useCaseFilter}
              setUseCaseFilter={setUseCaseFilter}
              sortBy={sortBy}
              setSortBy={setSortBy}
              searchRef={searchRef}
              filterMode={filterMode}
              setFilterMode={setFilterMode}
              hideInactive={hideInactive}
              setHideInactive={setHideInactive}
            />

            <div className="p-2 lg:p-6 flex-1 flex flex-col">
              <ResourceList
                resources={paginatedResources}
                expandedId={expandedId}
                onToggleExpand={toggleExpand}
                onEdit={openEdit}
                onDelete={handleDelete}
                isMine={isMine}
                onViewManagers={async (r) => {
                  // container handles fetching per-resource managers (same logic as before)
                  try {
                    setPerResourceManagers([]);
                    setPerResourceManagersLoading(true);
                    setModalManagersResource(r);
                    setManagersModalOpen(true);
                    if (Array.isArray(r.managers) && r.managers.length > 0) {
                      setPerResourceManagers(r.managers.map(normalizeManager));
                    } else {
                      const res = await axiosInstance.get(`/user/resource-manager?resource=${encodeURIComponent(r.id)}`);
                      const data = res.data?.data || res.data || [];
                      setPerResourceManagers((Array.isArray(data) ? data : []).map(normalizeManager));
                    }
                  } catch (err) {
                    console.error('Failed to fetch managers for resource', err);
                    toast.error('Failed to load managers for resource');
                    setPerResourceManagers([]);
                  } finally {
                    setPerResourceManagersLoading(false);
                  }
                }}
                getManagerEntry={getManagerEntry}
                getManagerEntries={getManagerEntries}
                 managersLoading={managersLoading}
                 isAddEnabled={isAddResourceEnabled}
                 isAdmin={isAdmin}
                 copyId={copyId}
                 copyJSON={copyJSON}
                 formatRoleLabel={formatRoleLabel}
               />

              {visibleResources.length > 0 && (
                <div>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    itemsPerPage={pageSize}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handlePageSizeChange}
                    itemsPerPageOptions={[5, 10, 20, 50]}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Resource"
        description={`Are you sure you want to delete "${deleteConfirm?.name || 'this resource'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
        iconBg="bg-red-100"
        iconColor="text-red-600"
        requireTextConfirmation={true}
        confirmationText={deleteConfirm?.name || ''}
        confirmationPlaceholder="Type the resource name to confirm"
      />

      <ManagersModalComponent
        open={managersModalOpen && !!modalManagersResource}
        resource={modalManagersResource}
        managers={perResourceManagers}
        loading={perResourceManagersLoading}
        onClose={() => { setManagersModalOpen(false); setModalManagersResource(null); setPerResourceManagers([]); }}
        onCopyEmail={(email) => { navigator.clipboard.writeText(email || ''); toast.info('Copied email'); }}
      />

      <AllManagersModalComponent
        open={allManagersModalOpen}
        groupedManagers={groupedManagers}
        loading={managersLoading}
        onClose={() => setAllManagersModalOpen(false)}
        onCopyEmail={(email) => { navigator.clipboard.writeText(email || ''); toast.info('Copied email'); }}
      />
    </div>
  );
}
